import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { HandHeart, Loader2, Play, Send, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { txt } from "@/lib/site";
import { Chapter, MaskLine, Reveal } from "@/components/motion";

const inputCls = "w-full border border-slate-200 rounded-xl p-3 md:p-4 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 text-slate-800 font-medium bg-white transition";

function youtubeId(url) {
  const m = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{6,})/.exec(url || "");
  return m ? m[1] : null;
}

export function Sermons({ items = [], site }) {
  const [active, setActive] = useState(null);
  if (!items.length) return null;
  return (
    <section id="sermon" data-testid="sermon-section" className="bg-slate-950 py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Reveal><Chapter n="07" label={txt(site, "sermon_label", "Sermon")} dark /></Reveal>
        <h2 className="mt-10 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[0.95]">
          <MaskLine inView>{txt(site, "sermon_heading_1", "Khotbah")}</MaskLine><MaskLine inView delay={0.1}>{txt(site, "sermon_heading_2", "terbaru")}</MaskLine>
        </h2>
        <p className="mt-5 text-white/50 max-w-md">Dengarkan kembali Firman yang disampaikan di GIM Church Makassar. Tautan video dikelola langsung dari Portal OPC.</p>

        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {items.map((s, i) => {
            const id = youtubeId(s.youtube_url);
            return (
              <motion.button key={s.id} onClick={() => setActive(s)} data-testid={`sermon-card-${s.id}`}
                initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="group text-left rounded-[1.75rem] overflow-hidden border border-white/10 bg-white/5 hover:border-amber-400/40 transition">
                <span className="relative block aspect-video bg-slate-900 overflow-hidden">
                  {id && <img src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`} alt="" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition duration-700" />}
                  <span className="absolute inset-0 grid place-items-center"><span className="w-14 h-14 rounded-full bg-amber-400 text-slate-900 grid place-items-center shadow-lg group-hover:scale-110 transition"><Play className="w-6 h-6 ml-0.5" /></span></span>
                </span>
                <span className="block p-6">
                  {s.series && <span className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-300">{s.series}</span>}
                  <span className="block mt-2 text-lg font-black text-white leading-tight">{s.title}</span>
                  <span className="block mt-1 text-sm text-white/45">{s.speaker} · {fmtDate(s.date)}</span>
                </span>
              </motion.button>
            );
          })}
        </div>
      </div>

      {active && (
        <div className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-sm grid place-items-center p-4" data-testid="sermon-player-dialog" onClick={() => setActive(null)}>
          <div className="w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div><p className="text-xs font-black uppercase tracking-[0.2em] text-amber-300">{active.series || "Sermon"}</p><p className="mt-1 text-xl font-black text-white">{active.title}</p><p className="text-sm text-white/50">{active.speaker} · {fmtDate(active.date)}</p></div>
              <button onClick={() => setActive(null)} data-testid="sermon-player-close" className="w-10 h-10 shrink-0 rounded-full bg-white/10 text-white grid place-items-center"><X className="w-5 h-5" /></button>
            </div>
            <div className="mt-4 aspect-video rounded-2xl overflow-hidden bg-black">
              {youtubeId(active.youtube_url)
                ? <iframe title={active.title} src={`https://www.youtube.com/embed/${youtubeId(active.youtube_url)}?rel=0`} className="w-full h-full" allowFullScreen allow="accelerometer; autoplay; encrypted-media; picture-in-picture" />
                : <a href={active.youtube_url} target="_blank" rel="noreferrer" className="w-full h-full grid place-items-center text-amber-300 font-bold">Buka video khotbah →</a>}
            </div>
            {active.description && <p className="mt-4 text-sm text-white/50">{active.description}</p>}
          </div>
        </div>
      )}
    </section>
  );
}

export function ServeUpInvite() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({ name: "", phone: "", email: "", ministry_id: "", role: "", answers: {} });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || form) return;
    api.get("/public/ministry/serve-form").then((r) => {
      setForm(r.data);
      setValues((v) => ({ ...v, ministry_id: r.data.categories[0]?.id || "" }));
    }).catch((e) => toast.error(errMsg(e)));
  }, [open, form]);

  const roles = form?.categories.find((c) => c.id === values.ministry_id)?.roles || [];
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/public/ministry/serve-up", values);
      toast.success("Terima kasih! Tim pastoral akan menghubungi Anda.");
      setOpen(false);
      setValues({ name: "", phone: "", email: "", ministry_id: form.categories[0]?.id || "", role: "", answers: {} });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="serve-up-invite" className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
      <Reveal>
        <div className="rounded-[2rem] bg-slate-900 text-white p-8 md:p-12 flex flex-col md:flex-row md:items-center gap-8">
          <div className="flex-1">
            <p className="text-[11px] font-black uppercase tracking-[0.3em] text-amber-400 flex items-center gap-2"><HandHeart className="w-4 h-4" />Rindu melayani?</p>
            <h3 className="mt-4 text-3xl md:text-4xl font-black tracking-tight">Setiap orang dipanggil melayani</h3>
            <p className="mt-3 text-white/60 max-w-lg">Daftarkan diri Anda di divisi pelayanan GIM Church. Tim pastoral akan menghubungi dan mengarahkan langkah berikutnya.</p>
          </div>
          <button onClick={() => setOpen(true)} data-testid="serve-up-open-button" className="shrink-0 inline-flex items-center justify-center gap-2 bg-amber-400 text-slate-900 font-bold rounded-full px-8 py-4 shadow-lg hover:bg-amber-300 active:scale-95 transition">
            <HandHeart className="w-5 h-5" />Serve-Up
          </button>
        </div>
      </Reveal>

      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" data-testid="serve-up-public-dialog">
          <form onSubmit={submit} className="w-full max-w-lg bg-white rounded-3xl p-6 md:p-8 space-y-4 my-8" data-testid="serve-up-public-form">
            <div className="flex items-start justify-between">
              <div><h3 className="text-xl font-black text-slate-900">Form Serve-Up</h3><p className="text-xs text-slate-500 mt-1">Isi data Anda, tim pastoral akan menindaklanjuti.</p></div>
              <button type="button" onClick={() => setOpen(false)} data-testid="serve-up-public-close" className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            {!form ? <p className="text-sm text-slate-400">Memuat form…</p> : (
              <>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input className={inputCls} placeholder="Nama lengkap" required value={values.name} onChange={(e) => setValues({ ...values, name: e.target.value })} data-testid="serve-public-name" />
                  <input className={inputCls} placeholder="No. WhatsApp" required value={values.phone} onChange={(e) => setValues({ ...values, phone: e.target.value })} data-testid="serve-public-phone" />
                </div>
                <input className={inputCls} type="email" placeholder="Email (opsional)" value={values.email} onChange={(e) => setValues({ ...values, email: e.target.value })} data-testid="serve-public-email" />
                <select className={inputCls} required value={values.ministry_id} onChange={(e) => setValues({ ...values, ministry_id: e.target.value, role: "" })} data-testid="serve-public-category">
                  {form.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {roles.length > 0 && (
                  <select className={inputCls} value={values.role} onChange={(e) => setValues({ ...values, role: e.target.value })} data-testid="serve-public-role">
                    <option value="">Role yang diminati (opsional)…</option>
                    {roles.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                )}
                {form.questions.map((q) => {
                  const set = (val) => setValues((v) => ({ ...v, answers: { ...v.answers, [q.id]: val } }));
                  const val = values.answers[q.id] || "";
                  return (
                    <label key={q.id} className="block" data-testid={`serve-question-${q.id}`}>
                      <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{q.label}{q.required && " *"}</span>
                      {q.type === "textarea" ? (
                        <textarea rows={3} className={`${inputCls} mt-2`} placeholder={q.placeholder} required={q.required} value={val} onChange={(e) => set(e.target.value)} />
                      ) : q.type === "select" ? (
                        <select className={`${inputCls} mt-2`} required={q.required} value={val} onChange={(e) => set(e.target.value)}>
                          <option value="">Pilih…</option>{q.options.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : (
                        <input className={`${inputCls} mt-2`} placeholder={q.placeholder} required={q.required} value={val} onChange={(e) => set(e.target.value)} />
                      )}
                    </label>
                  );
                })}
                <button disabled={loading} data-testid="serve-up-public-submit" className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 text-white font-bold py-3 md:py-4 rounded-xl shadow-md hover:bg-blue-700 active:scale-95 transition disabled:opacity-60">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" />Kirim Pendaftaran</>}
                </button>
              </>
            )}
          </form>
        </div>
      )}
    </section>
  );
}
